import Button from "@components/Button";
import Icon from "@components/Icon";
import { Input } from "@components/Input";
import { Select } from "@components/Select";
import { COMPANY_ID } from "@constants/auth.constant";
import { APP_LOGIN, FORGETPASSWORD, HOME, SIGNUP } from "@constants/internal-route.constant";
import { useAuth } from "@context/Auth";
import { AuthHead, AuthLinks } from "@layout/AuthHead";
import { AuthService } from "@services/api/Auth.service";
import { LocalStorageService } from "@services/utils/localStorage.service";
import { ToastService } from "@services/utils/toast.service";
import { ENV } from "config/ENV.config";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";

type Company = {
  companyId: string;
  companyName: string;
  roleName?: string;
};

const Login = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { saveAuth } = useAuth();
  const { state } = useLocation();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [showVerificationBtn, setShowVerificationBtn] = useState<boolean>(false);
  const [pendingUsername, setPendingUsername] = useState<string>("");

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm();

  // A workspace choice is only needed when the account belongs to more than one company.
  const needsCompanyChoice = companies.length > 1;

  // Editing the email/username restarts the workspace step so we never sign in to a stale company.
  const resetCompanyStep = () => {
    if (companies.length || selectedCompanyId || showVerificationBtn) {
      setCompanies([]);
      setSelectedCompanyId("");
      setShowVerificationBtn(false);
    }
  };

  // Final step — the password is verified here against the chosen workspace.
  const doLogin = (username: string, password: string, companyId: string) => {
    setIsLoading(true);
    LocalStorageService.set(COMPANY_ID, companyId + "");
    AuthService.login({
      username,
      password,
      companyId,
      appName: ENV.productCode || "",
    })
      .then((resp) => {
        setShowVerificationBtn(false);
        ToastService.success(resp?.msg);
        saveAuth(resp?.body?.initialToken, { ...resp?.body });
        if (state?.isAppLogin) {
          // Hand off through the /app-login screen so the redirect runs on a real
          // route (showing "Redirecting to the app…") instead of flashing the
          // "Page not found" catch-all while still sitting on /login.
          navigate(APP_LOGIN);
        } else {
          navigate(state?.from || HOME);
        }
      })
      .catch((err) => {
        // 501 => the account exists but isn't verified yet
        setShowVerificationBtn(Number(err?.code) === 501);
        ToastService.error(err?.msg);
      })
      .finally(() => setIsLoading(false));
  };

  const onSubmit = (formData) => {
    const username: string = formData.username;
    const password: string = formData.password;

    // Step 2 — multiple workspaces are on screen; require an explicit selection.
    if (needsCompanyChoice) {
      if (!selectedCompanyId) {
        ToastService.error("Please select your company to continue.");
        return;
      }
      doLogin(username, password, selectedCompanyId);
      return;
    }

    // Already resolved to a single workspace — sign straight in.
    if (companies.length === 1) {
      doLogin(username, password, companies[0].companyId);
      return;
    }

    // Step 1 — password is already entered; resolve which workspace(s) this account can access.
    setIsLoading(true);
    setPendingUsername(username);
    AuthService.getCompanyList({ username })
      .then((resp) => {
        const list: Company[] = resp?.body || [];
        if (!list.length) {
          ToastService.error("No workspace is linked to this account.");
          setIsLoading(false);
          return;
        }
        setCompanies(list);
        if (list.length === 1) {
          // Single company — go with [0], no selection needed.
          doLogin(username, password, list[0].companyId);
        } else {
          // More than one — let the user pick.
          setSelectedCompanyId("");
          setIsLoading(false);
        }
      })
      .catch((err) => {
        ToastService.error(err?.msg);
        setIsLoading(false);
      });
  };

  const sendVerificationLink = () => {
    setIsLoading(true);
    AuthService.sendVerificationLink({
      username: pendingUsername || getValues("username"),
      companyId: selectedCompanyId || companies?.[0]?.companyId,
      appName: ENV.productCode || "",
    })
      .then((resp) => {
        setShowVerificationBtn(false);
        ToastService.success(resp?.msg);
      })
      .catch((err) => ToastService.error(err?.msg))
      .finally(() => setIsLoading(false));
  };

  const usernameReg = register("username", {
    required: "Please provide your email or username",
  });

  return (
    <>
      <AuthHead title="Welcome back">Sign in to open your workspace.</AuthHead>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Input
          label="Email/Username"
          autoFocus={true}
          registerProperty={{
            ...usernameReg,
            onChange: (e) => {
              usernameReg.onChange(e);
              resetCompanyStep();
            },
          }}
          isError={!!errors?.username}
          errorMessage={errors?.username?.message as string}
        />

        <Input
          label="Password"
          type="password"
          registerProperty={{
            ...register("password", { required: "Please enter your password" }),
          }}
          isError={!!errors?.password}
          errorMessage={errors?.password?.message as string}
        />
        <div className="erp-auth-forgot">
          <Link to={FORGETPASSWORD}>Forgot password?</Link>
        </div>

        {needsCompanyChoice && (
          <>
            <div className="alert alert-info d-flex gap-2 small py-2" role="status">
              <Icon icon="building" />
              <span>This account can access more than one workspace. Choose where to sign in.</span>
            </div>
            <Select
              label="Workspace"
              isRequired={true}
              options={companies}
              valueKey="companyId"
              textKey="companyName"
              placeholder="Select your workspace"
              value={selectedCompanyId}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
            />
          </>
        )}

        {showVerificationBtn && (
          <div className="mb-2">
            <Button className="w-100" variant="outline" color="success" type="button" size="lg" onClick={() => sendVerificationLink()}>
              <Icon icon="envelope-check" /> Send Verification Link
            </Button>
          </div>
        )}

        <div>
          <Button
            className="w-100 mt-3"
            color="primary"
            type="submit"
            size="lg"
            isLoading={isLoading}
            loadingText={needsCompanyChoice ? "Signing in..." : "Checking account..."}
          >
            {needsCompanyChoice ? "Sign in" : "Continue"}
          </Button>
        </div>

        {ENV.signupEnabled ? (
          <AuthLinks>
            <span>
              Don’t have an account? <Link to={SIGNUP}>Start a 30-day trial</Link>
            </span>
          </AuthLinks>
        ) : null}
      </form>
    </>
  );
};

export default Login;
